import React, { useState, useEffect } from "react";
import { CSpinner } from "@coreui/react-pro";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as EyeIcon } from "../../assets/icons/eye.svg";
import { ReactComponent as EyeSlashIcon } from "../../assets/icons/eye-slash.svg";
import { getRoleNames, editAdmin } from "../../utils/backend";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useField } from "formik";
import { getAdmin } from "../../utils/backend";
import { toast } from "react-toastify";

const EditAdmin = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [roleNames, setRoleNames] = useState([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const { id } = useParams();
  const [admin, setAdmin] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({
    username: "",
    full_name: "",
    role: "",
    email: "",
    phone_number: "",
    country_code: "+60",
  });
  const [originalValues, setOriginalValues] = useState({});

  useEffect(() => {
    fetchData(id);
  }, [id]);

  const fetchData = async (id) => {
    setIsLoading(true);
    try {
      const response = await getAdmin({ id: id });
      const fetchedValues = {
        username: response.data.username || "",
        full_name: response.data.full_name || "",
        role: response.data.role || "",
        email: response.data.email || "",
        phone_number: response.data.phone_number.startsWith("+60")
          ? response.data.phone_number.substring(3)
          : response.data.phone_number || "",
        country_code: response.data.phone_number.startsWith("+60") ? "+60" : "",
        vehicle_type: response.data.vehicle_type || "",
      };
      setInitialValues(fetchedValues);
      setOriginalValues(fetchedValues);
    } catch (error) {
      console.error("Error fetching data:", error);
      setAdmin({});
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchRoleNames = async () => {
      setIsLoadingRoles(true);
      try {
        const response = await getRoleNames();
        setRoleNames(response.data);
      } catch (error) {
        console.error("Failed to fetch role names:", error);
      } finally {
        setIsLoadingRoles(false);
      }
    };

    fetchRoleNames();
  }, []);

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .required("Username is required")
      .min(3, "Username must be between 3 and 30 characters")
      .max(30, "Username must be between 3 and 30 characters")
      .matches(
        /^[a-zA-Z0-9_]+$/,
        "Username can only contain letters, numbers, and underscores"
      ),
    full_name: Yup.string()
      .required("Full name is required")
      .min(3, "Full Name must be between 3 and 50 characters")
      .max(50, "Full Name must be between 3 and 50 characters"),
    role: Yup.string().required("Role is required"),
    email: Yup.string()
      .email("Invalid email")
      .required("Email is required")
      .max(100, "Email must be less than 100 characters"),
    phone_number: Yup.string()
      .required("Phone number is required")
      .matches(
        /^\+?\d{8,13}$/,
        'Phone Number must be between 10 and 15 digits and can optionally start with a "+"'
      ),
    password: Yup.string()
      .min(6, "Password must be between 6 and 100 characters")
      .max(100, "Password must be between 6 and 100 characters"),
  });

  const togglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleSubmit = async (
    values,
    { setSubmitting, setErrors, setFieldTouched }
  ) => {
    setIsSubmittingForm(true);
    try {
      const updatedValues = Object.keys(values).reduce((acc, key) => {
        if (values[key] !== originalValues[key]) {
          acc[key] = values[key];
        }
        return acc;
      }, {});

      updatedValues.id = id;
      updatedValues.phone_number = values.country_code + values.phone_number;

      if (Object.keys(updatedValues).length > 1) {
        await editAdmin(updatedValues);
        toast.success("Admin updated successfully!");
        navigate(`/admins/details/${updatedValues.id}`);
      } else {
        toast.info("No changes detected.");
        navigate(`/admins/details/${updatedValues.id}`);
      }
    } catch (error) {
      console.error("Failed to edit admin:", error);
      toast.error(error.message || "Failed to edit admin. Please try again.");
      setErrors({ submit: "Failed to edit admin", ...error.fieldErrors });
      Object.keys(values).forEach((field) =>
        setFieldTouched(field, true, false)
      );
    } finally {
      setSubmitting(false);
      setIsSubmittingForm(false);
    }
  };

  const handleBackClick = () => {
    const currentPath = window.location.pathname;
    if (currentPath.includes(`/details/${id}/edit`)) {
      navigate(`/admins/details/${id}`);
    } else {
      navigate("/admins");
    }
  };

  return (
    <>
      {/* <h5 className='mb-3'>Edit Admin</h5> */}
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body p-4">
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Skeleton
                height={40}
                width={100}
                style={{ marginRight: "10px" }}
              />
              <Skeleton height={40} width={100} />
            </div>
          </div>
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form>
              {isSubmittingForm && (
                <div className="form-overlay">
                  <CSpinner color="primary" />
                </div>
              )}
              <div className="card border-light">
                <div className="card-body">
                  {errors.submit && (
                    <div className="alert alert-danger">{errors.submit}</div>
                  )}
                  {/* Form fields */}
                  <div className="row">
                    {/* Username */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="username" className="form-label">
                        <span className="text-danger">*</span> Username
                      </label>{" "}
                      <Field
                        name="username"
                        as="input"
                        type="text"
                        placeholder="Enter username"
                        className={`form-control ${
                          touched.username && errors.username
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="username"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                    {/* Full Name */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="full_name" className="form-label">
                        <span className="text-danger">*</span> Full Name
                      </label>{" "}
                      <Field
                        name="full_name"
                        as="input"
                        type="text"
                        placeholder="Enter full name"
                        className={`form-control ${
                          touched.full_name && errors.full_name
                            ? "is-invalid"
                            : ""
                        }`}
                      />
                      <ErrorMessage
                        name="full_name"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                  <div className="row">
                    {/* Role */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="role" className="form-label">
                        <span className="text-danger">*</span> Role
                      </label>{" "}
                      <div className="input-group">
                        <Field
                          name="role"
                          as="select"
                          className={`form-select ${
                            touched.role && errors.role ? "is-invalid" : ""
                          }`}
                          disabled={isLoadingRoles}
                        >
                          <option value="">Select a role</option>
                          {roleNames.map((roleName) => (
                            <option key={roleName} value={roleName}>
                              {roleName}
                            </option>
                          ))}
                        </Field>
                        {isLoadingRoles && (
                          <span className="input-group-text">
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Loading...
                              </span>
                            </div>
                          </span>
                        )}
                        <ErrorMessage
                          name="role"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                    {/* Email */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="email" className="form-label">
                        <span className="text-danger">*</span> Email
                      </label>{" "}
                      <Field
                        name="email"
                        as="input"
                        type="email"
                        placeholder="Enter email"
                        className={`form-control ${
                          touched.email && errors.email ? "is-invalid" : ""
                        }`}
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </div>
                  <div className="row">
                    {/* Phone Number */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="phone_number" className="form-label">
                        <span className="text-danger">*</span> Phone Number
                      </label>
                      <div className="input-group">
                        <Field
                          as="select"
                          name="country_code"
                          className="form-select country-code-dropdown"
                        >
                          <option value="+60">+60</option>
                        </Field>
                        <Field
                          name="phone_number"
                          as="input"
                          type="tel"
                          placeholder="xxxxxxxxx"
                          className={`form-control ${
                            touched.phone_number && errors.phone_number
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <ErrorMessage
                          name="phone_number"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                    <style jsx>{`
                      .country-code-dropdown {
                        width: auto;
                        max-width: 80px;
                      }
                    `}</style>
                    {/* Password */}
                    <div className="col-md-6 mb-3">
                      <label htmlFor="password" className="form-label">
                        Password
                      </label>
                      <div className="input-group">
                        <Field
                          name="password"
                          as="input"
                          type={isPasswordVisible ? "text" : "password"}
                          placeholder="Leave blank if password is unchanged"
                          className={`form-control ${
                            touched.password && errors.password
                              ? "is-invalid"
                              : ""
                          }`}
                        />
                        <span
                          className="input-group-text"
                          onClick={togglePasswordVisibility}
                          style={{ cursor: "pointer" }}
                        >
                          {isPasswordVisible ? (
                            <EyeIcon
                              style={{ width: "20px", height: "20px" }}
                            />
                          ) : (
                            <EyeSlashIcon
                              style={{ width: "20px", height: "20px" }}
                            />
                          )}
                        </span>
                        <ErrorMessage
                          name="password"
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </div>
                  </div>
                  <div className=" d-flex justify-content-end">
                    <button
                      type="button"
                      className="btn me-3 ansa-button-secondary"
                      onClick={handleBackClick}
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      className="btn ansa-button-primary"
                      disabled={isSubmitting}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </>
  );
};

export default EditAdmin;
